import React from 'react';
import { BookOpenIcon, CreditCardIcon, LockKeyholeIcon, ShieldCheckIcon, SparklesIcon, ZapIcon } from 'lucide-react';
import { motion, useReducedMotion } from 'framer-motion';
import { CTAButton } from './Shared';
import { HerbSprig, LeafCluster, Mandala, MortarPestle } from './HealingIcons';
const productMockupUrl = 'https://cdn.magicpatterns.com/uploads/mWTuvE47PoqkYMryatpyWz/Mockup_Botiquin_Ancestral.png';
export function Hero() {
  const reduceMotion = useReducedMotion();
  return <header className="relative isolate overflow-hidden pb-8 pt-3 sm:pt-7 md:pb-14 md:pt-16" data-id="element-130" data-mp="xa4h5bx7i9f2">
      <Mandala className="pointer-events-none absolute -right-20 top-4 -z-10 h-72 w-72 opacity-[0.11] md:right-6 md:h-96 md:w-96" color="#B88A35" data-id="element-131" data-mp="sart4nulmwaw" />
      <Mandala className="pointer-events-none absolute -bottom-24 -left-24 -z-10 h-80 w-80 opacity-[0.09] md:h-[30rem] md:w-[30rem]" color="#2E5946" data-id="element-132" data-mp="nekqp51ew2xp" />
      <HerbSprig className="absolute left-[7%] top-48 hidden h-14 w-14 opacity-35 md:block" color="#647D56" data-id="element-133" data-mp="3814alm6t1smr" />
      <LeafCluster className="absolute right-[7%] top-56 hidden h-16 w-16 opacity-30 md:block" color="#2E5946" data-id="element-134" data-mp="3o7lqjmmw8ymu" />

      <div className="mx-auto max-w-7xl px-4 text-center sm:px-6 lg:px-8" data-id="element-135" data-mp="399ba3kjqp7ue">
        <motion.div initial={reduceMotion ? false : {
        opacity: 0,
        y: 18
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.55,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-136" data-mp="3e963pvuwgbj5">
          <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-brand-gold/55 bg-brand-paper px-3 py-1 shadow-sm sm:mb-5 sm:px-4 sm:py-1.5" data-id="element-137" data-mp="2viy1rh2xrr4j">
            <HerbSprig className="h-3.5 w-3.5 sm:h-4 sm:w-4" color="#2E5946" data-id="element-138" data-mp="2hjoux80me9d8" />
            <span className="font-serif text-[10px] font-semibold tracking-[0.16em] text-brand-ink sm:text-xs" data-id="element-139" data-mp="2ljpef72ktar5">
              LUMEN AKASHA™
            </span>
          </div>

          <p className="mb-2 font-serif text-sm font-semibold tracking-[0.08em] text-brand-forest sm:mb-4 sm:text-base" data-id="element-140" data-mp="nat65811aej4">
            El Botiquín Ancestral™
          </p>
          <h1 className="mx-auto max-w-3xl font-serif text-3xl font-semibold leading-[1.05] text-brand-ink sm:text-5xl md:text-6xl" data-id="element-141" data-mp="i9vpx0zl3uk9">
            ¿Trabajás con{' '}
            <span className="font-bold text-brand-purpleDark" data-id="element-142" data-mp="1b0drfxkntnvk">Tuina</span> o{' '}
            <span className="font-bold text-brand-purpleDark" data-id="element-143" data-mp="162ued0zp7h57">Digitopuntura?</span>
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-brand-text sm:text-lg md:text-xl" data-id="element-144" data-mp="111ok2yqe3ltk">
            Tené siempre a mano el punto que necesitás con una guía visual
            simple, organizada y fácil de consultar.
          </p>

          <div className="relative mx-auto mt-3 w-[calc(100%+2rem)] max-w-[34rem] -translate-x-4 sm:mt-8 sm:w-full sm:max-w-4xl sm:translate-x-0" data-id="element-145" data-mp="3lqm4i498ffxk">
            <MortarPestle className="absolute -left-5 top-1/2 hidden h-12 w-12 -translate-y-1/2 opacity-50 md:block" color="#B66735" data-id="element-146" data-mp="1xacf521xbhsp" />
            <motion.img src={productMockupUrl} alt="Mockup de El Botiquín Ancestral con ebook, fichas visuales y seis bonos de regalo" initial={reduceMotion ? false : {
            opacity: 0,
            scale: 0.96
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.5,
            delay: reduceMotion ? 0 : 0.12,
            ease: [0.22, 1, 0.36, 1]
          }} className="mx-auto h-auto max-h-[43svh] w-full object-contain drop-shadow-[0_18px_22px_rgba(34,54,70,0.22)] sm:max-h-[42rem] sm:max-w-3xl md:max-w-4xl" draggable={false} decoding="async" data-id="element-147" data-mp="269hegalb7f5x" />
          </div>

          <div className="mx-auto mt-3 flex max-w-xl flex-wrap justify-center gap-x-3 gap-y-1.5 text-[11px] font-medium text-brand-forest sm:mt-4 sm:text-xs" data-id="element-148" data-mp="3vhxg7je3l7yj">
            <span data-id="element-149" data-mp="e8schd8aoufv">📚 50+ fichas prácticas</span>
            <span data-id="element-150" data-mp="99xntunncbnu">🎁 6 bonos de regalo</span>
            <span data-id="element-151" data-mp="9b8o3khvv1a7">📄 100% PDF</span>
            <span data-id="element-152" data-mp="3hqabqwa04t4o">🖨️ Listo para imprimir</span>
          </div>

          <div className="mt-4 flex justify-center sm:hidden" data-id="element-153" data-mp="3hqjz0c3nwm6o">
            <CTAButton className="w-full max-w-[22rem] px-5 py-3 text-[13px] leading-tight" data-id="element-154" data-mp="3crz1wljpiimg">
              → QUIERO MI BOTIQUÍN ANCESTRAL AHORA
            </CTAButton>
          </div>

          <div className="mt-5 hidden flex-col items-center gap-5 sm:flex" data-id="element-155" data-mp="3motuyox7nqtc">
            <CTAButton className="px-7 py-4 text-sm md:px-10 md:text-lg" data-id="element-156" data-mp="2yzsw9te6ju19">
              → QUIERO MI BOTIQUÍN ANCESTRAL AHORA
            </CTAButton>
            <div className="flex max-w-3xl flex-wrap justify-center gap-x-4 gap-y-2 text-xs text-brand-muted md:text-sm" data-id="element-157" data-mp="2tzwpxni0fqij">
              <span className="inline-flex items-center gap-1.5" data-id="element-158" data-mp="19hchk9janxc7">
                <ShieldCheckIcon className="h-4 w-4 text-brand-forest" data-id="element-159" data-mp="1eafbiirzh8sx" />
                Garantía 7 días
              </span>
              <span className="inline-flex items-center gap-1.5" data-id="element-160" data-mp="zmas1cl3wt93">
                <BookOpenIcon className="h-4 w-4 text-brand-forest" data-id="element-161" data-mp="14isq5tlxltcg" />
                Listo para usar en tablet o imprimir
              </span>
              <span className="inline-flex items-center gap-1.5" data-id="element-162" data-mp="1s7298nrrg2zw">
                <LockKeyholeIcon className="h-4 w-4 text-brand-forest" data-id="element-163" data-mp="1x6x0wtjd0wm0" />
                Pago seguro
              </span>
              <span className="inline-flex items-center gap-1.5" data-id="element-164" data-mp="1j874lg4d70yp">
                <CreditCardIcon className="h-4 w-4 text-brand-forest" data-id="element-165" data-mp="1n90rq79zgbqj" />
                Mercado Pago
              </span>
            </div>
            <div className="flex flex-wrap justify-center gap-2 text-xs font-semibold text-brand-ink md:text-sm" data-id="element-166" data-mp="7s0dz724clfy">
              <span className="rounded-full border border-brand-green/30 bg-brand-paper px-3 py-2" data-id="element-167" data-mp="bsk9k671ngzz">
                <BookOpenIcon className="mr-1 inline h-3.5 w-3.5 text-brand-forest" data-id="element-168" data-mp="2ejeqfwo2312h" />
                100% PDF
              </span>
              <span className="rounded-full border border-brand-green/30 bg-brand-paper px-3 py-2" data-id="element-169" data-mp="24r93508r17tm">
                <ZapIcon className="mr-1 inline h-3.5 w-3.5 text-brand-forest" data-id="element-170" data-mp="1ztfve2rss5r8" />
                Acceso en 5 min
              </span>
              <span className="rounded-full border border-brand-green/30 bg-brand-paper px-3 py-2" data-id="element-171" data-mp="1vsnjt2vgdors">
                <SparklesIcon className="mr-1 inline h-3.5 w-3.5 text-brand-forest" data-id="element-172" data-mp="1qqes09ubgsr9" />
                Para todos los niveles
              </span>
            </div>
          </div>
        </motion.div>
      </div>
    </header>;
}